import { Button as FplButton } from '@figma/fpl-components';
import { Typography, type TypographyProps } from '@/UI/AppTypography/AppTypography';
import { color as _color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { ReactNode } from 'react';

interface ButtonTextPlainProps {
  children?: ReactNode;
  variant?: TypographyProps['variant'];
  disabled?: boolean;
  color?: string;
  onClick: () => void;
}

/**
 * A text control styled like a link but semantically a button (click action, no
 * navigation). `fpl-migration` boundary: flag-off renders the legacy underlined
 * `Typography`; flag-on renders an accessible FPL `Button` link variant
 * (`destructiveLink` when the caller color signals a destructive/error action).
 */
export const ButtonTextPlain = ({
  children,
  onClick,
  variant = 'body-std-rg',
  color = _color.White100,
  disabled,
}: ButtonTextPlainProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled === true) {
    const fplVariant = color === _color.Weavy_Error ? 'destructiveLink' : 'link';
    return (
      <FplButton variant={fplVariant} size="md" disabled={disabled} onClick={onClick}>
        {children}
      </FplButton>
    );
  }

  return (
    <Typography
      variant={variant}
      color={color}
      sx={{
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: 'none',
        },
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onClick={onClick}
    >
      {children}
    </Typography>
  );
};
